import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { Heart, GraduationCap, Music, Users } from "lucide-react";
import Badge from "../components/Badge";
import Section from "../components/Section";
import Seo from "../components/Seo";
import { fadeUp, springSoft, staggerContainer, viewportOnce } from "../lib/motion";
import { creches } from "../data/creches";
import { site } from "../data/site";

const MotionLink = motion.create(Link);

type Job = {
  slug: string;
  titre: string;
  contrat: string;
  diplome: string;
  description: string;
  missions: string[];
  applyUrl: string;
};

const jobs: Job[] = [
  {
    slug: "apprentie-petite-enfance",
    titre: "Apprenti·e petite enfance",
    contrat: "Alternance",
    diplome:
      "Aucun diplôme requis pour nos crèches de l'Eure (27) — bac pro ASSP ou SAPAT obligatoire pour nos crèches de Seine-Maritime (76)",
    description:
      "L'apprenti·e accompagne les enfants au quotidien et participe à la mise en œuvre du projet pédagogique musical La La Land.",
    missions: [
      "Travailler en accord avec le projet pédagogique",
      "Veiller à la sécurité et au bien-être des enfants",
      "Effectuer les tâches d'entretien et de soins",
      "Accueillir les enfants et échanger avec les parents",
      "Mettre en place des activités d'éveil et de motricité",
      "Garantir l'hygiène et le bon maintien des équipements",
    ],
    applyUrl: "https://lalalandformulaire.3117.fr/recrutement/alternance",
  },
  {
    slug: "stagiaire-petite-enfance",
    titre: "Stagiaire petite enfance",
    contrat: "Stage",
    diplome: "CAP Petite Enfance ou équivalent",
    description:
      "Le ou la stagiaire accompagne les enfants et observe la mise en œuvre du projet pédagogique sous supervision de l'équipe.",
    missions: [
      "Assister à l'application du projet pédagogique",
      "Contribuer à la surveillance et au bien-être des enfants",
      "Participer aux tâches d'entretien et de soins",
      "Accueillir les enfants et observer les échanges parents",
      "Aider à la mise en place des activités d'éveil",
      "Aider à maintenir l'hygiène des équipements",
    ],
    applyUrl: "https://lalalandformulaire.3117.fr/recrutement/stage",
  },
  {
    slug: "professionnelle-petite-enfance-cdi",
    titre: "Professionnel·le petite enfance",
    contrat: "CDI",
    diplome: "DEEJE, Aux. de Puériculture, ou CAP AEPE + 2 ans d'expérience",
    description:
      "La ou le professionnel qualifié·e assure l'accueil quotidien et l'accompagnement individualisé des enfants. Garant·e de l'application du projet pédagogique au sein de la section.",
    missions: [
      "Réaliser l'ensemble des soins quotidiens (repas, hygiène, sommeil)",
      "Veiller à la sécurité physique et affective des enfants",
      "Appliquer le projet pédagogique au quotidien",
      "Animer des activités d'éveil (motricité, langage, sensoriel)",
      "Observer le développement et transmettre à l'équipe",
      "Assurer la transmission quotidienne aux parents",
      "Contribuer à la cohésion et la dynamique de l'équipe",
    ],
    applyUrl: "https://lalalandformulaire.3117.fr/recrutement/cdi",
  },
];

const values = [
  {
    Icon: Music,
    title: "Pédagogie musicale",
    text: "Une intervenante musicale chaque semaine, des comptines pour rythmer la journée, et une vraie identité pédagogique.",
  },
  {
    Icon: Heart,
    title: "Équipes en or",
    text: "5 professionnel·les par crèche, des plannings stables, et une vraie place pour chaque voix dans le projet.",
  },
  {
    Icon: Users,
    title: "14 enfants maximum",
    text: "Une taille humaine qui permet un accompagnement individualisé et une relation de qualité avec chaque famille.",
  },
  {
    Icon: GraduationCap,
    title: "Formation continue",
    text: "Apprentissage, alternance, montée en compétences. Nous formons aussi nos propres talents.",
  },
];

const process = [
  {
    n: "01",
    title: "Candidature en ligne",
    text: "Vous remplissez le formulaire dédié au poste qui vous intéresse, en quelques minutes.",
  },
  {
    n: "02",
    title: "Échange téléphonique",
    text: "Premier contact avec la direction sous 7 jours pour comprendre votre parcours et vos envies.",
  },
  {
    n: "03",
    title: "Rencontre en crèche",
    text: "Visite de la structure, immersion sur une demi-journée et échange avec l'équipe.",
  },
];

const jobPostingJsonLd = jobs.map((j) => ({
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: j.titre,
  description: `${j.description} ${j.missions.join(". ")}.`,
  datePosted: new Date().toISOString().slice(0, 10),
  employmentType: j.contrat === "CDI" ? "FULL_TIME" : "INTERN",
  hiringOrganization: {
    "@type": "Organization",
    name: site.legalName,
    sameAs: site.url,
    logo: `${site.url}/images/logo.webp`,
  },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressRegion: "Normandie",
      addressCountry: "FR",
    },
  },
  qualifications: j.diplome,
  directApply: true,
  url: j.applyUrl,
}));

export default function NousRejoindre() {
  const subject = encodeURIComponent("Candidature spontanée — La La Land");
  const body = encodeURIComponent(
    "Bonjour,\n\nJe vous adresse ma candidature spontanée pour rejoindre l'équipe La La Land.\n\n— Prénom / Nom :\n— Diplôme et expérience :\n— Crèche(s) ou ville(s) qui m'intéresse(nt) :\n— Disponibilité :\n\nVous trouverez mon CV en pièce jointe.\n\nMerci d'avance.",
  );
  const mailto = `mailto:${site.organization.contactEmail}?subject=${subject}&body=${body}`;

  return (
    <>
      <Seo
        path="/nous-rejoindre"
        title="Recrutement — Rejoindre l'équipe La La Land"
        description="La La Land recrute en Normandie : apprenti·e, stagiaire et professionnel·le petite enfance (CDI). Nos micro-crèches, pédagogie musicale, équipes en or."
        jsonLd={jobPostingJsonLd}
      />

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge tone="musical" className="mb-6">
              Recrutement · Normandie
            </Badge>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-ink">
              Rejoignez l'équipe La La Land.
            </h1>
            <p className="mt-6 text-lg md:text-xl text-ink-soft max-w-xl leading-relaxed">
              Vous êtes passionné·e par la petite enfance et la musique vous parle ? Nous recrutons toute l'année dans nos micro-crèches normandes.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#postes-ouverts">
                <button className="bg-sage hover:bg-sage-hover text-white px-8 py-4 text-lg rounded-xl font-medium transition active:scale-[0.97]">
                  Candidature spontanée
                </button>
              </a>
            </div>
          </div>
          <div className="rounded-4xl aspect-[4/3] overflow-hidden shadow-soft-md">
            <img
              src="/images/lalaland-prairie.jpg"
              alt="Les lettres La La Land installées dans une prairie en Normandie"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <Section
        eyebrow="Pourquoi nous rejoindre"
        title="Travailler chez La La Land, c'est…"
        description="Une vision claire, des équipes stables et un projet pédagogique qui change le quotidien des enfants comme des pros."
      >
        <motion.div
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {values.map(({ Icon, title, text }) => (
            <motion.article
              key={title}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className="bg-surface border border-edge-soft rounded-3xl p-6 shadow-soft-sm transition-shadow hover:shadow-soft-md"
            >
              <div className="w-11 h-11 rounded-2xl bg-sage-soft text-sage-hover grid place-items-center mb-4">
                <Icon size={22} strokeWidth={1.7} />
              </div>
              <h3 className="font-display text-xl font-semibold text-ink leading-tight">
                {title}
              </h3>
              <p className="mt-2 text-ink-soft leading-relaxed">{text}</p>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <Section
        eyebrow="Postes ouverts"
        title="Trois manières de rejoindre l'aventure"
        description="Vous trouverez ci-dessous les postes en cours dans le réseau. Chaque candidature est étudiée par la direction et la directrice de la crèche concernée."
        className="scroll-mt-20"
      >
        <motion.div
          id="postes-ouverts"
          className="grid md:grid-cols-3 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {jobs.map((j) => (
            <motion.article
              key={j.slug}
              variants={fadeUp}
              className="bg-surface border border-edge-soft rounded-3xl p-7 shadow-soft-sm flex flex-col"
            >
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Badge tone="terra">{j.contrat}</Badge>
                <Badge tone="neutral">Toutes nos crèches</Badge>
              </div>
              <h3 className="font-display text-xl font-semibold text-ink leading-tight">
                {j.titre}
              </h3>
              <p className="mt-3 text-ink-soft leading-relaxed">
                {j.description}
              </p>
              <div className="mt-5 pt-5 border-t border-edge-soft">
                <p className="text-xs uppercase tracking-wider font-semibold text-ink-muted mb-1.5">
                  Diplôme
                </p>
                <p className="text-sm text-ink leading-snug">{j.diplome}</p>
              </div>
              <div className="mt-5">
                <p className="text-xs uppercase tracking-wider font-semibold text-ink-muted mb-2">
                  Missions
                </p>
                <ul className="space-y-1.5">
                  {j.missions.slice(0, 4).map((m) => (
                    <li
                      key={m}
                      className="text-sm text-ink-soft leading-snug pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-terra"
                    >
                      {m}
                    </li>
                  ))}
                  {j.missions.length > 4 && (
                    <li className="text-sm text-ink-muted italic pl-4">
                      … +{j.missions.length - 4} autres
                    </li>
                  )}
                </ul>
              </div>
              <a
                href={j.applyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center justify-center bg-terra hover:bg-terra-hover text-white px-5 py-3 rounded-xl font-medium transition active:scale-[0.97]"
              >
                Postuler
              </a>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <Section eyebrow="Process" title="Notre process de recrutement">
        <motion.div
          className="grid md:grid-cols-3 gap-5"
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {process.map((p) => (
            <motion.article
              key={p.n}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={springSoft}
              className="bg-surface border border-edge-soft rounded-3xl p-7 shadow-soft-sm transition-shadow hover:shadow-soft-md"
            >
              <p className="font-display text-2xl text-terra font-semibold">
                {p.n}
              </p>
              <h3 className="mt-3 font-display text-xl font-semibold text-ink leading-tight">
                {p.title}
              </h3>
              <p className="mt-3 text-ink-soft leading-relaxed">{p.text}</p>
            </motion.article>
          ))}
        </motion.div>
      </Section>

      <Section
        eyebrow="Nos implantations"
        title="Nos crèches en Seine-Maritime et Eure"
        description="Choisissez la crèche la plus proche de chez vous. Toutes nos crèches recrutent à différents moments de l'année."
      >
        <motion.div
          className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
          variants={staggerContainer(0.05)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
        >
          {creches.map((c) => (
            <MotionLink
              key={c.slug}
              to={`/micro-creches/${c.slug}`}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              transition={springSoft}
              className="bg-surface border border-edge-soft rounded-2xl p-4 shadow-soft-sm hover:border-terra hover:shadow-soft-md transition group"
            >
              <p className="font-display text-base font-semibold text-ink group-hover:text-terra transition-colors leading-tight">
                {c.nom}
              </p>
              <p className="text-xs text-ink-muted mt-1">{c.ville}</p>
            </MotionLink>
          ))}
        </motion.div>
      </Section>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="bg-gradient-to-br from-honey to-honey-hover text-ink rounded-4xl p-10 md:p-16 shadow-soft-lg">
            <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-2xl">
              Aucune offre ne vous correspond ?
            </h2>
            <p className="mt-4 text-lg opacity-90 max-w-xl">
              Envoyez-nous une candidature spontanée : nous gardons les profils intéressants pour les ouvertures à venir.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={mailto}>
                <button className="bg-ink text-white px-6 py-3 rounded-xl font-medium hover:opacity-90 transition active:scale-[0.97]">
                  Candidature spontanée
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
